import { type ReactElement, useEffect } from "react";
import type { FieldValues } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useStatsigClient } from "@statsig/react-bindings";
import { useRouter } from "next/router";

import { analytics } from "@flare/analytics";

import type { FunnelAnswers, SubPractices } from "@/modules/v2/funnel/config";
import { funnelConfig } from "@/modules/v2/funnel/config";
import { FunnelLayout } from "@/modules/v2/funnel/FunnelLayout";
import { useFunnelAnswers } from "@/modules/v2/funnel/hooks/useFunnelAnswers";
import { SubPracticeStep } from "@/modules/v2/funnel/steps/sub-practice";
import { BiEventsNames } from "@/services/analytics/event-names";

export default function Practice() {
  const { push, query } = useRouter();
  const methods = useForm();
  const { funnelAnswers, setFunnelAnswers } = useFunnelAnswers();

  const practice = query.practice as FunnelAnswers["practice"];

  // Track funnel view event
  useEffect(() => {
    analytics.track(BiEventsNames.WebFunnelView, {
      practice,
      current_step_key: "matter-related",
    });
  }, [practice]);

  /* ~~~~~~~~~~~~~~~~ Start: Statsig POC ~~~~~~~~~~~~~~~~ */
  const { getExperiment } = useStatsigClient();

  getExperiment("aa_start_funnel_all_population_v2");
  /* ~~~~~~~~~~~~~~~~ End: Statsig POC ~~~~~~~~~~~~~~~~ */

  async function onSubmit(data: FieldValues) {
    const { subPractice } = data;

    const currentSubPractice: SubPractices =
      // @ts-expect-error: implicit any
      funnelConfig[practice]?.[subPractice];

    const { firstScreen } = currentSubPractice;

    analytics.track(BiEventsNames.WebFunnelClick, {
      practice,
      sub_practice: subPractice,
      current_step_key: "matter-related",
      answer_value: Object.entries(data).flat().toString(),
      full_answer: JSON.stringify(data),
      click_type: "submit",
    });

    setFunnelAnswers({
      ...funnelAnswers,
      practice: funnelAnswers.practice || practice,
      subPractice,
    });

    await push(`/v2/funnel/${practice}/${subPractice}/${firstScreen}`);
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <SubPracticeStep
          answers={{
            ...funnelAnswers,
            practice: funnelAnswers.practice || practice,
          }}
        />
      </form>
    </FormProvider>
  );
}

Practice.getLayout = function getLayout(page: ReactElement) {
  return <FunnelLayout>{page}</FunnelLayout>;
};
